import React, { useState } from 'react';
import { Agent } from '../../../../../types/agent';

interface PublishGlobalModalProps {
  agent: Agent;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

/**
 * Modal de confirmação para publicar agente globalmente.
 *
 * Mostra:
 * - Aviso de que o agente ficará visível a TODOS os workspaces
 * - Lista de verificação (nome, descrição, prompt configurados)
 * - Confirmação obrigatória
 */
export const PublishGlobalModal: React.FC<PublishGlobalModalProps> = ({
  agent,
  isOpen,
  onClose,
  onConfirm
}) => {
  const [isPublishing, setIsPublishing] = useState(false);

  const handlePublish = async () => {
    setIsPublishing(true);
    try {
      await onConfirm();
      onClose();
    } catch (err) {
      // Error already handled by parent
      console.error('Error publishing agent:', err);
    } finally {
      setIsPublishing(false);
    }
  };

  if (!isOpen) return null;

  // Check if agent is complete
  const isAgentComplete = Boolean(
    agent.name &&
    agent.description &&
    (agent.use_guided_prompt ? agent.prompt_sections : agent.custom_prompt)
  );

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 w-screen h-screen bg-black/60 z-40"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
        <div className="bg-[#0a0a0a] border border-[#333333] rounded-lg shadow-xl max-w-md w-full">
          <div className="p-6">
            <h2 className="text-xl font-bold text-white mb-4">
              Publicar Agente Globalmente
            </h2>

            {/* Warning */}
            <div className="bg-yellow-900/20 border border-yellow-600 rounded-lg p-4 mb-6">
              <p className="text-yellow-200 text-sm">
                ⚠️ Este agente ficará visível e utilizável por TODAS as empresas da plataforma.
              </p>
            </div>

            {/* Readiness Checklist */}
            <div className="mb-6">
              <h3 className="font-semibold text-white mb-3 text-sm">
                Verificação de Prontidão:
              </h3>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-sm">
                  <span className={agent.name ? 'text-green-400' : 'text-red-400'}>
                    {agent.name ? '✅' : '❌'}
                  </span>
                  <span className="text-[#999999]">Nome definido</span>
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <span className={agent.description ? 'text-green-400' : 'text-red-400'}>
                    {agent.description ? '✅' : '❌'}
                  </span>
                  <span className="text-[#999999]">Descrição definida</span>
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <span className={
                    (agent.use_guided_prompt ? agent.prompt_sections : agent.custom_prompt)
                      ? 'text-green-400'
                      : 'text-red-400'
                  }>
                    {(agent.use_guided_prompt ? agent.prompt_sections : agent.custom_prompt)
                      ? '✅'
                      : '❌'}
                  </span>
                  <span className="text-[#999999]">Prompt configurado</span>
                </li>
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-[#1a1a1a] text-white rounded-lg border border-[#333333] hover:bg-[#2a2a2a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isPublishing}
              >
                Cancelar
              </button>
              <button
                onClick={handlePublish}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isPublishing || !isAgentComplete}
                title={!isAgentComplete ? 'Agente deve estar completo para ser publicado' : ''}
              >
                {isPublishing ? 'Publicando...' : 'Confirmar Publicação'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PublishGlobalModal;
